import React from "react";
import { Toaster } from "sonner";
import { Users } from "lucide-react";
import ArcadeNav from "./ArcadeNav";
import GameCard from "./GameCard";
import ArcadeFooter from "./ArcadeFooter";
import { GAMES } from "./games";
import { usePresenceByGame } from "../game/presence";
import useSEO from "../seo/useSEO";
import { SEO_HOME } from "../seo/seoData";
import "./arcade.css";

// The /  and /arcade root. Shows a featured ZC3D hero card on top and a
// grid of all other games (live + coming-soon) below.
export default function ArcadeHome() {
  useSEO(SEO_HOME);
  const featured = GAMES.find((g) => g.featured) || GAMES[0];
  const rest = GAMES.filter((g) => g.id !== featured.id);
  const presence = usePresenceByGame();
  const counts = presence.games || {};
  const total = presence.total || 0;
  return (
    <div className="arcade-page" data-testid="arcade-home">
      <Toaster position="top-center" theme="dark" />
      <ArcadeNav />

      <div className="arcade-bg" aria-hidden="true">
        <div className="arcade-bg-glow arcade-bg-glow-1" />
        <div className="arcade-bg-glow arcade-bg-glow-2" />
        <div className="arcade-bg-grid" />
      </div>

      <main className="arcade-main">
        <section className="arcade-hero" data-testid="arcade-hero">
          <h1 className="arcade-hero-title">
            Welcome to the <span className="accent">Zombie Arcade</span>
          </h1>
          <p className="arcade-hero-sub">
            One login. One wallet. A growing roster of zombie games to lose your nights to.
          </p>
          {total > 0 && (
            <div className="arcade-hero-online" data-testid="arcade-hero-online">
              <span className="online-dot" aria-hidden="true" />
              <Users size={14} />
              <b>{total}</b>
              <span>player{total === 1 ? "" : "s"} online across the arcade</span>
            </div>
          )}
        </section>

        <section className="arcade-section" data-testid="arcade-featured">
          <h2 className="arcade-section-title">Featured</h2>
          <GameCard game={featured} featured onlineCount={counts[featured.id] || 0} />
        </section>

        <section className="arcade-section" data-testid="arcade-grid">
          <h2 className="arcade-section-title">All Games</h2>
          <div className="arcade-cards">
            {rest.map((g) => (
              <GameCard key={g.id} game={g} onlineCount={counts[g.id] || 0} />
            ))}
          </div>
        </section>

        <footer className="arcade-footer">
          <section className="arcade-seo-blurb" aria-label="About Zombie Arcade">
            <h2>About Zombie Arcade</h2>
            <p>
              Zombie Arcade is a free browser-based games portal built around a single zombie-apocalypse universe.
              One sign-in, one shared coin wallet, and a growing roster of titles spanning genres — 3D arcade
              action, classic Klondike solitaire, match-3 puzzles, tower defense and more. Every game runs
              instantly in your browser. No downloads. No installs. No paywalls on core gameplay.
            </p>
            <p>
              Our currently live games include <strong>Zombie Collector 3D</strong>, a Three.js-powered UFO
              tractor-beam survival game with 11 themed worlds and endless mode; and{" "}
              <strong>Zombie Solitaire</strong>, a Klondike card game with a hand-drawn zombie suit theme,
              drag-and-drop controls and auto-finish. Coins earned in any game spend in any other game&apos;s shop —
              cosmetics, themes and perks all share a single wallet across the arcade.
            </p>
            <p>
              Optional <strong>VIP</strong> and <strong>Founder</strong> tiers unlock quality-of-life perks
              (unlimited undos, carry-over upgrades, bonus coin multipliers) but every core game stays free
              forever. Play on desktop, tablet or phone — the games are built mobile-first with touch
              controls and responsive layouts.
            </p>
          </section>
        </footer>
        <ArcadeFooter />
      </main>
    </div>
  );
}
